import React, { useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import SectionContainer from '../SectionContainer';
import Field, { hasDataInFields } from '../../Field';
import { IconArrow, IconReview, IconTrustpilot } from '../../../../components';
import Slider from 'react-slick';

import css from './SectionReviews.module.css';
import client1Image from './images/1.png';
import client2Image from './images/2.png';
import client3Image from './images/3.png';

const NextArrow = ({ onClick }) => (
  <div className={classNames(css.arrow, css.arrowRight)} onClick={onClick}>
    <IconArrow direction="right" />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div className={classNames(css.arrow, css.arrowLeft)} onClick={onClick}>
    <IconArrow direction="left" />
  </div>
);

const SectionReviews = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
    intl,
  } = props;

  const [currentSlide, setCurrentSlide] = useState(0);

  const SLIDER_SETTINGS = {
    arrows: false,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: index => setCurrentSlide(index),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const reviews = [
    {
      title: intl.formatMessage({ id: 'LandingPage.SectionReviews.review1.title' }),
      review: intl.formatMessage({ id: 'LandingPage.SectionReviews.review1.review' }),
      ratingTitle: 'Ausgezeichnet',
      rating: 5,
      count: 266,
      client: {
        image: client1Image,
        name: intl.formatMessage({ id: 'LandingPage.SectionReviews.review1.clientName' }),
        bio: intl.formatMessage({ id: 'LandingPage.SectionReviews.review1.clientBio' }),
      },
    },
    {
      title: intl.formatMessage({ id: 'LandingPage.SectionReviews.review2.title' }),
      review: intl.formatMessage({ id: 'LandingPage.SectionReviews.review2.review' }),
      ratingTitle: 'Ausgezeichnet',
      rating: 5,
      count: 266,
      client: {
        image: client2Image,
        name: intl.formatMessage({ id: 'LandingPage.SectionReviews.review2.clientName' }),
        bio: intl.formatMessage({ id: 'LandingPage.SectionReviews.review2.clientBio' }),
      },
    },
    {
      title: intl.formatMessage({ id: 'LandingPage.SectionReviews.review3.title' }),
      review: intl.formatMessage({ id: 'LandingPage.SectionReviews.review3.review' }),
      ratingTitle: 'Ausgezeichnet',
      rating: 5,
      count: 266,
      client: {
        image: client3Image,
        name: intl.formatMessage({ id: 'LandingPage.SectionReviews.review3.clientName' }),
        bio: intl.formatMessage({ id: 'LandingPage.SectionReviews.review3.clientBio' }),
      },
    },
  ];

  const hasReviews = !!reviews && reviews?.length > 0;
  const currentReview = reviews[currentSlide];

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, css.whyUs)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.container}>
        <div className={css.left}>
          {hasHeaderFields ? (
            <header className={classNames(css.header, defaultClasses.sectionDetails)}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}

          <div className={css.reviewWrapper}>
            <div className={css.reviewBlock}>
              {/* <div className={css.reviewTitle}>{currentReview.title}</div> */}
              <div className={css.review}>{currentReview.review}</div>
            </div>
            <div className={css.rating}>
              <div className={css.ratingTitle}>
                {currentReview.ratingTitle}
                <span title={`${currentReview.rating}/5`}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <IconReview
                      key={`star-${star}`}
                      className={css.reviewRatingStar}
                      isFilled={star <= currentReview.rating}
                    />
                  ))}
                </span>
              </div>

              <div className={css.ratingCount}>
                {intl.formatMessage(
                  { id: 'LandingPage.SectionReviews.count' },
                  { count: <span>{currentReview.count}</span> }
                )}
              </div>
              <div className={css.trustpilot}>
                <IconTrustpilot />
                <span>{intl.formatMessage({ id: 'LandingPage.SectionReviews.trustpilot' })}</span>
              </div>
            </div>
          </div>
        </div>

        {hasReviews ? (
          <div className={css.right}>
            <Slider {...SLIDER_SETTINGS} className={css.slider}>
              {reviews.map(review => (
                <div className={css.reviewContainer}>
                  <div className={css.client}>
                    <img
                      className={css.clientImage}
                      alt={review.client.name}
                      src={review.client.image}
                    />
                    <div className={css.clientDetails}>
                      <div className={css.name}>{review.client.name}</div>
                      <div className={css.description}>{review.client.bio}</div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : null}
      </div>
    </SectionContainer>
  );
};

SectionReviews.defaultProps = {
  className: null,
  rootClassName: null,
};

SectionReviews.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
};

export default SectionReviews;
